import { supabase } from '../lib/supabase'
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'
import { getPresentationById } from './presentationsService'

const TABLE_NAME = 'presentation_slides'

/**
 * Get slide counts per presentation (for list page). Returns { [presentationId]: number }.
 */
export const getSlideCountsByPresentationIds = async (presentationIds, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  if (!Array.isArray(presentationIds) || presentationIds.length === 0) {
    return { data: {}, error: null }
  }

  const { data: rows, error } = await supabase
    .from(TABLE_NAME)
    .select('presentation_id')
    .in('presentation_id', presentationIds)

  if (error) {
    return { data: null, error: { message: error.message, code: error.code } }
  }

  const counts = {}
  presentationIds.forEach((id) => { counts[id] = 0 })
  ;(rows || []).forEach((r) => {
    if (r.presentation_id && counts[r.presentation_id] != null) {
      counts[r.presentation_id] += 1
    }
  })
  return { data: counts, error: null }
}

/**
 * List slides for a presentation. Access inherited via presentation ownership (RLS).
 */
export const listSlidesByPresentation = async (presentationId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { data: presentation, error: presError } = await getPresentationById(presentationId, options)
  if (presError || !presentation) {
    return { data: null, error: presError || { message: 'Presentation not found.', code: 'not_found' } }
  }

  const query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('presentation_id', presentationId)
    .order('slide_number', { ascending: true })

  return handleQuery(query, { fallbackMessage: 'Unable to load slides.' })
}

/**
 * Create a slide. Ensures user can manage the presentation first.
 */
export const createSlide = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { data: presentation, error: presError } = await getPresentationById(payload.presentation_id, options)
  if (presError || !presentation) {
    return { data: null, error: presError || { message: 'Presentation not found.', code: 'not_found' } }
  }

  const query = supabase.from(TABLE_NAME).insert(payload).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to create the slide.',
    allowEmpty: false
  })
}

/**
 * Update a slide.
 */
export const updateSlide = async (slideId, updates, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { data: existing } = await supabase.from(TABLE_NAME).select('presentation_id').eq('id', slideId).maybeSingle()
  if (!existing) {
    return { data: null, error: { message: 'Slide not found.', code: 'not_found' } }
  }

  const { error: presError } = await getPresentationById(existing.presentation_id, options)
  if (presError) {
    return { data: null, error: presError }
  }

  const query = supabase.from(TABLE_NAME).update(updates).eq('id', slideId).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to update the slide.',
    allowEmpty: false
  })
}

/**
 * Delete a slide.
 */
export const deleteSlide = async (slideId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { data: existing } = await supabase.from(TABLE_NAME).select('presentation_id').eq('id', slideId).maybeSingle()
  if (!existing) {
    return { data: null, error: { message: 'Slide not found.', code: 'not_found' } }
  }

  const { error: presError } = await getPresentationById(existing.presentation_id, options)
  if (presError) {
    return { data: null, error: presError }
  }

  const query = supabase.from(TABLE_NAME).delete().eq('id', slideId).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the slide.',
    allowEmpty: false
  })
}

/**
 * Reorder slides: update slide_number for multiple slides. Pass array of { id, slide_number }.
 */
export const reorderSlides = async (presentationId, orderedSlides, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const { error: presError } = await getPresentationById(presentationId, options)
  if (presError) {
    return { data: null, error: presError }
  }

  const updates = (Array.isArray(orderedSlides) ? orderedSlides : []).map((s, i) => ({
    id: s.id,
    slide_number: s.slide_number != null ? s.slide_number : i
  }))

  for (const u of updates) {
    const { error } = await supabase
      .from(TABLE_NAME)
      .update({ slide_number: u.slide_number })
      .eq('id', u.id)
      .eq('presentation_id', presentationId)
    if (error) {
      return {
        data: null,
        error: { message: error.message || 'Failed to reorder slides.', code: error.code }
      }
    }
  }

  return listSlidesByPresentation(presentationId, options)
}
